import { Controller } from "stimulus";
import Rails from "@rails/ujs";
import { debounce } from "lodash";

export default class extends Controller {
  static targets = [
    'assignedResourcesList',
    'fileField',
    'fileInput',
    'linkField',
    'linkInput',
    'newResourceForm',
    'newResourceSection',
    'newResourceButtons',
    'duplicateWarningSection',
    'scormCloudCourseField',
    'resourcesFallbackContainer',
    'searchSection',
    'aiAssistantField'
  ];

  static values = {
    assignedToType: String,
    assignedToId: Number,
    programId: String,
    searchUrl: String
  };

  connect() {
    // FIXME dropdown component doesn't accept data attributes so we can't add targets
    this.scormCloudCourseInput = this.newResourceFormTarget
      .querySelector('input[name="resource[scorm_cloud_course_id]"]');

    this.aiAssistantInput = this.newResourceFormTarget
      .querySelector('input[name="resource[ai_assistant_id]"]');

    this.fileInput = this.newResourceFormTarget
      .querySelector('input[name="resource[file]"]');

    this.typeInput = this.newResourceFormTarget
      .querySelector('input[name="resource[type]"]');

    this.linkInputChangeHandler = debounce(this._handleLinkInputChange, 300).bind(this);
    this.videoLinkInputChangeHandler = debounce(this._handleVideoLinkInputChange, 300).bind(this);
    this.typeInputChangeHandler = this._handleTypeChange.bind(this);
    this.fileInputChangeHandler = this._handleFileInputChange.bind(this);
    this.aiAssistantInputChangeHandler = this._handleAiAssistantInputChange.bind(this);
    this.scormCloudInputChangeHandler = this._handleScormCloudInputChange.bind(this);

    document.addEventListener(
      'promote:editor--resources:created', this.handleResourceCreated.bind(this)
    );

    document.addEventListener(
      'promote:editor--resources:assigned', this.handleResourceAssigned.bind(this)
    );

    document.addEventListener(
      'promote:editor--resources:show-duplicate-warning', this.handleDuplicateSection.bind(this)
    );

    document.addEventListener(
      'promote:editor--resources:no-duplicates', this.closeDuplicateWarning.bind(this));
    this.searchSectionIsOpen = false;
  }

  handleDuplicateSection(event) {
    if (this.assignedToTypeValue != event.detail.assignedToType ||
      this.assignedToIdValue != event.detail.assignedToId) {
      return;
    }

    this._hideNewResourceButtons();
    this.duplicateWarningSectionTarget.classList.remove('hidden');

    if (this.duplicateWarningSectionTarget.children.length) {
      this.duplicateWarningSectionTarget.children[0].classList.remove('hidden');
    }
  }

  handleResourceCreated(event) {
    if (this.assignedToTypeValue != event.detail.assignedToType ||
      this.assignedToIdValue != event.detail.assignedToId) {
      return;
    }

    this._hideNewResourceSection();
    this.newResourceFormTarget.reset();
    this.assignedResourcesListTarget.insertAdjacentHTML('beforeend', event.detail.element);
    this.resourcesFallbackContainerTarget.classList.add('hidden');
    this._showSearchSectionAfterCancel();
  }

  handleResourceAssigned(event) {
    if (this.assignedToTypeValue != event.detail.assignedToType ||
      this.assignedToIdValue != event.detail.assignedToId) {
      return;
    }

    this.assignedResourcesListTarget.insertAdjacentHTML('beforeend', event.detail.element);

    this.resourcesFallbackContainerTarget.classList.add('hidden');

    if (!this.duplicateWarningSectionTarget.classList.contains('hidden')) {
      this._hideDuplicateWarningSection();
      this._hideNewResourceSection();
      this.newResourceFormTarget.reset();
      this._showSearchSectionAfterCancel();
    }
  }

  closeDuplicateWarning(event) {
    const assignedToType = event?.
      detail?.assignedToType || event.currentTarget.dataset.assignedToType;

    const assignedToId = event?.
      detail?.assignedToId || event.currentTarget.dataset.assignedToId;

    if (this.assignedToTypeValue != assignedToType ||
      this.assignedToIdValue != assignedToId) {
      return;
    }

    this._hideDuplicateWarningSection();
    this._showNewResourceButtons();
    this._enableSubmitButton();
  }

  _hideDuplicationSectionOnDropdownChange() {
    this._hideDuplicateWarningSection();
    this._showNewResourceButtons();
    this._disableSubmitButton();
  }

  dispatchDuplicationCheck(type, data) {
    const formData = new FormData();
    formData.append(type, data);
    formData.append('resource_type', type);
    formData.append('assigned_to_id', this.assignedToIdValue);
    formData.append('assigned_to_type', this.assignedToTypeValue);

    Rails.ajax({
      url: `/admin/programs/${this.programIdValue}/editor/resources/verify`,
      type: 'POST',
      data: formData,
      processData: false,
      contentType: false,
      dataType: 'script',
      success: function(response) {
      },
      error: function(response) {
        console.error('Error verifying duplicate resource', response);
      }
    });
  }

  cancelNewResource() {
    this._verifyCreateForm(() => {
      this._hideNewResourceSection();
      this.newResourceFormTarget.reset();
      this._showSearchSectionAfterCancel();
    });
  }

  resetForm(event) {
    event.target.closest('form').reset();
  }

  _hideNewResourceSection() {
    this.newResourceSectionTarget.classList.add('hidden');
  }

  showNewResourceSection() {
    this.newResourceSectionTarget.classList.remove('hidden');
    this.searchSectionTarget.classList.add('hidden');
    this._hideDuplicateWarningSection();
    this._showNewResourceButtons();

    this.newResourceSectionTarget.scrollIntoView({ behaviour: 'smooth', block: 'nearest', inline: 'end' });

    this.typeInput.addEventListener('change', this.typeInputChangeHandler);
    this.fileInput.addEventListener('change', this.fileInputChangeHandler);
    this.aiAssistantInput.addEventListener('change', this.aiAssistantInputChangeHandler);
    this.scormCloudCourseInput.addEventListener('change', this.scormCloudInputChangeHandler);
  }

  _handleTypeChange(event) {
    const resourceType = event.target.value;

    if (resourceType === 'FileResource')  {
      this._enableInputTypeForm('file');
      this._hideDuplicationSectionOnDropdownChange();
    } else if (resourceType === 'LinkResource')  {
      this._enableInputTypeForm('link');
      this.linkInputTarget.value = '';
      this.linkInputTarget.removeEventListener('input', this.videoLinkInputChangeHandler);
      this.linkInputTarget.addEventListener('input', this.linkInputChangeHandler);
      this._hideDuplicationSectionOnDropdownChange();
    } else if (resourceType === 'VideoLinkResource')  {
      this._enableInputTypeForm('link');
      this.linkInputTarget.value = '';
      this.linkInputTarget.removeEventListener('input', this.linkInputChangeHandler);
      this.linkInputTarget.addEventListener('input', this.videoLinkInputChangeHandler);
      this._hideDuplicationSectionOnDropdownChange();
    } else if (resourceType ==='ScormCloudResource')  {
      this._enableInputTypeForm('scorm');
      this._hideDuplicationSectionOnDropdownChange();
    } else if (resourceType === 'AIAssistantResource')  {
      this._enableInputTypeForm('ai');
      this._hideDuplicationSectionOnDropdownChange();
    }
  }

  _handleFileInputChange(event) {
    if (event.target.files.length === 0) {
      this._hideDuplicationSectionOnDropdownChange();
      return;
    }

    this.dispatchDuplicationCheck('file', event.target.files[0]);
  }

  _handleAiAssistantInputChange(event) {
    if (this.aiAssistantInput.value === '') {
      this._hideDuplicationSectionOnDropdownChange();
      return;
    }

    this._enableSubmitButton();
    this.dispatchDuplicationCheck('ai_assistant', event.target.value);
  }

  _handleScormCloudInputChange() {
    if (this.scormCloudCourseInput.value === '') {
      this._hideDuplicationSectionOnDropdownChange();
      return;
    }

    this._enableSubmitButton();
    this.dispatchDuplicationCheck('scorm_cloud', event.target.value);
  }

  _handleLinkInputChange(event) {
    this._handleLinkUrlChange(event, 'link');
  }

  _handleVideoLinkInputChange(event) {
    this._handleLinkUrlChange(event, 'video_link');
  }

  _handleLinkUrlChange(event, resource_type) {
    if (event.target.value.trim() === '') {
      this._hideDuplicationSectionOnDropdownChange();
      return;
    }

    this.dispatchDuplicationCheck(resource_type, event.target.value);
  }

  _hideNewResourceButtons() {
    this.newResourceButtonsTarget.classList.add('hidden');
  }

  _showNewResourceButtons() {
    this.newResourceButtonsTarget.classList.remove('hidden');
  }

  showSearchSection() {
    if (!this.searchSectionTarget.classList.contains('hidden')) {
      return;
    }

    const newResourceFormIsOpen = !this.newResourceSectionTarget.classList.contains('hidden');

    if (newResourceFormIsOpen) {
      this._verifyCreateForm(() => {
        this._showSearchSection();
      });
    } else {
      this._showSearchSection();
    }
  }

  _showSearchSection() {
    this._hideNewResourceSection();
    this.newResourceFormTarget.reset();
    this._hideDuplicateWarningSection();
    this._removeNewResourceEvents();

    this.searchSectionTarget.classList.remove('hidden');

    this.searchSectionIsOpen = true;

    Rails.ajax({
      url: this.searchUrlValue,
      type: 'GET',
      dataType: 'script',
    });
  }

  _showSearchSectionAfterCancel() {
    if (!this.searchSectionIsOpen) {
      return;
    }

    this.showSearchSection();
  }

  _hideDuplicateWarningSection() {
    this.duplicateWarningSectionTarget.classList.add('hidden');
  }

  hideSearchSection(event) {
    event.preventDefault();

    this.searchSectionTarget.classList.add('hidden');
    this.searchSectionIsOpen = false;
  }

  _enableInputTypeForm(type) {
    const formTypes = {
      'scorm': this.scormCloudCourseInput,
      'link': this.linkInputTarget,
      'file': this.fileInputTarget,
      'ai': this.aiAssistantInputTarget
    }

    const fields = {
      'scorm': this.scormCloudCourseFieldTarget,
      'link': this.linkFieldTarget,
      'file': this.fileFieldTarget,
      'ai': this.aiAssistantFieldTarget
    }

    Object.entries(formTypes).forEach(([key, value]) => {
      if (key === type) {
        if (value) value.disabled = false;
        fields[key].classList.remove('hidden');
      } else {
        if (value) value.disabled = true;
        fields[key].classList.add('hidden');
      }
    });
  }

  _disableSubmitButton() {
    this.newResourceFormTarget.querySelector('button[type="submit"]').disabled = true
  }

  _enableSubmitButton() {
    this.newResourceFormTarget.querySelector('button[type="submit"]').disabled = false
  }

  _removeNewResourceEvents() {
    this.typeInput.removeEventListener('change', this.typeInputChangeHandler);
    this.fileInput.removeEventListener('change', this.fileInputChangeHandler);
    this.aiAssistantInput.removeEventListener('change', this.aiAssistantInputChangeHandler);
    this.scormCloudCourseInput.removeEventListener('change', this.scormCloudInputChangeHandler);
    this.linkInputTarget.removeEventListener('input', this.videoLinkInputChangeHandler);
    this.linkInputTarget.removeEventListener('input', this.linkInputChangeHandler);
  }

  async _verifyCreateForm(callback) {
    try {
      await this._verify();

      callback();
    } catch (e) {
      // no-op
    }
  }

  async _verify() {
    const unloadChecker = this.application
      .getControllerForElementAndIdentifier(document.body, "unload-checker");

    return await unloadChecker.verify();
  }
}
